import {useListSorting} from '@sollapay/ui';
import {Button, ContentSection, Icon, ListEmptySearch} from '@sollapay/ui/components';
import {ListExportMenu} from '@sollapay/web-core';
import {useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation, Trans} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {
  BuyerCardList,
  BuyerCardListSkeleton,
  BuyerListSectionHeader,
  BuyerTable,
  BuyerTableSkeleton
} from '@/features/buyers/components';
import {createBuyerExportColumns} from '@/features/buyers/config/buyer-export.config';
import {DEFAULT_BUYER_FILTER_STATE} from '@/features/buyers/config/buyer-filter.config';
import {BUYER_SORT_CONFIG} from '@/features/buyers/config/buyer-sorting.config';
import {
  isAddBuyerTargetPending,
  useAddBuyerTarget,
  useBuyerDeleteFlow,
  useBuyersQuery,
  useFilteredBuyers,
  usePaymentsQuery,
  usePurchaseBuyers
} from '@/features/buyers/hooks';
import {
  AddBuyerFlow,
  AddBuyerToPurchaseFlow,
  DeleteBuyerModal,
  EditBuyerModal,
  InitiatePaymentFlow
} from '@/features/buyers/modals';
import {buildBuyerTableRows, formatBuyerDisplayName} from '@/features/buyers/utils';
import {PURCHASE_PARAM} from '@/features/buyers/view/hooks';
import {useCurrentTrustAccount, useTrustTypeStrategy} from '@/features/trust-accounts/providers';
import {useListViewPreference} from '@/hooks';

import {BuyersEmptyState} from './BuyersEmptyState';

import type {BuyerFilterState} from '@/features/buyers/config/buyer-filter.config';
import type {BuyerTableRow} from '@/features/buyers/types/buyers.types';
import type {BuyerDTO, BuyerPurchaseDTO} from '@sollapay/types';
import type {FC} from 'react';

interface PaymentContext {
  buyer: BuyerDTO;
  purchase: BuyerPurchaseDTO;
}

export const BuyersTabContent: FC = () => {
  const {t, i18n} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  const {
    id: trustAccountId,
    refId: trustAccountRefId,
    name: trustAccountName,
    trustPurpose,
    generalSaleTransactionType
  } = useCurrentTrustAccount();
  const {allowsMultiplePurchases} = useTrustTypeStrategy();
  const addBuyerTarget = useAddBuyerTarget();
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<BuyerFilterState>(DEFAULT_BUYER_FILTER_STATE);
  const [paymentContext, setPaymentContext] = useState<PaymentContext | null>(null);
  const [editBuyers, setEditBuyers] = useState<BuyerDTO[] | null>(null);
  const deleteFlow = useBuyerDeleteFlow(trustAccountId);
  const openAddBuyerRef = useRef<(() => void) | null>(null);
  const openAddBuyerToPurchaseRef = useRef<(() => void) | null>(null);

  const {data: buyers = [], isLoading: isBuyersLoading} = useBuyersQuery(trustAccountId);
  const {data: payments = [], isLoading: isPaymentsLoading} = usePaymentsQuery(trustAccountId);

  const {buyers: paymentBuyers} = usePurchaseBuyers(
    trustAccountId,
    paymentContext?.purchase.id,
    paymentContext?.buyer.id
  );
  const isLoading = isBuyersLoading || isPaymentsLoading;
  const {view, setPreferredView} = useListViewPreference('buyers:view');

  const allRows = useMemo(
    () => buildBuyerTableRows(buyers, payments, trustPurpose, generalSaleTransactionType),
    [buyers, payments, trustPurpose, generalSaleTransactionType]
  );
  const filteredRows = useFilteredBuyers(allRows, searchText, filter);
  const {
    sorting,
    onSortingChange,
    sortedItems: sortedRows
  } = useListSorting(filteredRows, BUYER_SORT_CONFIG);

  const buyerExportColumns = useMemo(
    () => createBuyerExportColumns(t, i18n.language),
    [t, i18n.language]
  );

  const handleViewBuyer = useCallback(
    (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => {
      navigate(
        `/trust-accounts/${trustAccountId}/buyers/${buyer.id}?${PURCHASE_PARAM}=${purchase.id}`
      );
    },
    [navigate, trustAccountId]
  );

  const handleRequestPayment = useCallback(
    (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => setPaymentContext({buyer, purchase}),
    []
  );

  const isEmpty = buyers.length === 0 && !isLoading;

  const handleCloseEdit = useCallback(() => setEditBuyers(null), []);
  const handleBuyerUpdated = useCallback(
    (updatedBuyer: BuyerDTO, previousBuyerId: string) =>
      setEditBuyers(prev => prev?.map(b => (b.id === previousBuyerId ? updatedBuyer : b)) ?? null),
    []
  );

  const handleAddBuyer = useCallback(() => {
    if (addBuyerTarget.kind === 'existing-purchase') {
      openAddBuyerToPurchaseRef.current?.();
    } else if (addBuyerTarget.kind === 'new-purchase') {
      openAddBuyerRef.current?.();
    }
    // 'disabled'/'pending'/'error'/'missing-purchase': no affordance to open — the CTA is hidden
    // or disabled respectively.
  }, [addBuyerTarget]);

  return (
    <>
      {/* Captures onOpen into a ref without rendering any UI — keeps AddBuyerFlow
          at a stable tree position so flow state survives the empty→non-empty
          transition when the buyers query refetches after creation. */}
      <AddBuyerFlow
        trustAccountId={trustAccountId}
        trigger={onOpen => {
          openAddBuyerRef.current = onOpen;
          return null;
        }}
      />

      {addBuyerTarget.kind === 'existing-purchase' && (
        <AddBuyerToPurchaseFlow
          trustAccountId={trustAccountId}
          purchaseId={addBuyerTarget.purchaseId}
          trigger={onOpen => {
            openAddBuyerToPurchaseRef.current = onOpen;
            return null;
          }}
        />
      )}

      {isEmpty ? (
        <BuyersEmptyState
          onAddBuyer={addBuyerTarget.kind === 'disabled' ? undefined : handleAddBuyer}
          isAddBuyerPending={isAddBuyerTargetPending(addBuyerTarget)}
        />
      ) : (
        <>
          <ContentSection
            title={t('view.buyers.list.header.title')}
            subtitle={t('view.buyers.list.header.description')}
            action={
              addBuyerTarget.kind !== 'disabled' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddBuyer}
                  isLoading={isAddBuyerTargetPending(addBuyerTarget)}
                  icon={<Icon name="add" />}
                >
                  {t('view.buyers.empty.action')}
                </Button>
              )
            }
          >
            <div className="flex flex-col gap-6">
              <BuyerListSectionHeader
                searchText={searchText}
                onSearchChange={setSearchText}
                sorting={sorting}
                onSortingChange={onSortingChange}
                view={view}
                onViewChange={setPreferredView}
                filter={filter}
                onFilterChange={setFilter}
                exportMenu={
                  <ListExportMenu<BuyerTableRow>
                    rows={sortedRows}
                    columns={buyerExportColumns}
                    refId={trustAccountRefId}
                    entity="buyers"
                    sheetName={trustAccountName}
                    disabled={sortedRows.length === 0}
                  />
                }
              />

              {isLoading ? (
                view === 'card' ? (
                  <BuyerCardListSkeleton />
                ) : (
                  <BuyerTableSkeleton showsCoBuyersInOneRow={allowsMultiplePurchases} />
                )
              ) : sortedRows.length === 0 ? (
                <ListEmptySearch
                  title={t('view.buyers.list.emptySearch.title')}
                  subtitle={
                    <Trans
                      t={t}
                      i18nKey="view.buyers.list.emptySearch.subtitle"
                      values={{term: searchText}}
                      components={{
                        bold: <strong className="font-semibold text-fg-secondary" />,
                        br: <br />
                      }}
                    />
                  }
                  clearLabel={t('view.buyers.list.emptySearch.clear')}
                  onClear={() => setSearchText('')}
                />
              ) : (
                <>
                  {view === 'card' ? (
                    <BuyerCardList
                      rows={sortedRows}
                      onRequestPayment={handleRequestPayment}
                      onViewBuyer={handleViewBuyer}
                      onEditBuyer={setEditBuyers}
                      onDeleteBuyer={deleteFlow.open}
                      trustAccountId={trustAccountId}
                    />
                  ) : (
                    <BuyerTable
                      rows={sortedRows}
                      sorting={sorting}
                      onSortingChange={onSortingChange}
                      onRequestPayment={handleRequestPayment}
                      onViewBuyer={handleViewBuyer}
                      onEditBuyer={setEditBuyers}
                      onDeleteBuyer={deleteFlow.open}
                      trustAccountId={trustAccountId}
                      showsCoBuyersInOneRow={allowsMultiplePurchases}
                    />
                  )}
                </>
              )}
            </div>
          </ContentSection>

          {paymentContext && (
            <InitiatePaymentFlow
              trustAccountId={trustAccountId}
              buyers={paymentBuyers}
              payments={payments}
              isOpen={true}
              selectedPurchase={paymentContext.purchase}
              onClose={() => setPaymentContext(null)}
            />
          )}

          <EditBuyerModal
            trustAccountId={trustAccountId}
            buyers={editBuyers}
            onClose={handleCloseEdit}
            onBuyerUpdated={handleBuyerUpdated}
          />

          <DeleteBuyerModal
            buyerName={
              deleteFlow.target ? formatBuyerDisplayName(deleteFlow.target.buyer.fullName) : null
            }
            onConfirm={deleteFlow.confirm}
            onClose={deleteFlow.close}
            isLoading={deleteFlow.isLoading}
            error={deleteFlow.error}
          />
        </>
      )}
    </>
  );
};
